import { AlertCircle, AlertTriangle, CheckCircle2, HelpCircle, Info } from 'lucide-react';
import type React from 'react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent } from '~/components/ui/card';
import { Skeleton } from '~/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';

import { useAnalyticsExperimentHealth } from '../../hooks/use-experiment-analytics';
import type { AnalyticsSensitivity } from '../../types/analytics-controls.types';
import type { AnalyticsHealthFlag, AnalyticsHealthFlagSeverity } from '../../types/analytics.types';
import { healthFlagMessage, healthFlagTitle } from '../../utils/health-flag-display';

interface Props {
  experimentId: string;
  sensitivity: AnalyticsSensitivity;
}

interface SeverityStyle {
  wrap: string;
  iconColor: string;
  Icon: typeof AlertTriangle;
}

/**
 * `Partial<Record<...>>` (rather than a closed `Record<...>`) so an unknown
 * runtime severity returns `undefined` from the lookup — which lets the
 * caller substitute the fallback style without TypeScript collapsing the
 * guard into dead code.
 */
const SEVERITY_STYLES: Partial<Record<AnalyticsHealthFlagSeverity, SeverityStyle>> = {
  critical: {
    wrap: 'border-error/40 bg-error-50',
    iconColor: 'text-error',
    Icon: AlertCircle,
  },
  warning: {
    wrap: 'border-warning/40 bg-warning-50',
    iconColor: 'text-warning',
    Icon: AlertTriangle,
  },
  info: {
    wrap: 'border-border bg-muted/30',
    iconColor: 'text-muted-foreground',
    Icon: Info,
  },
};

/** Default visual style when the backend ships a new severity we don't recognise. */
const FALLBACK_SEVERITY_STYLE: SeverityStyle = {
  wrap: 'border-border bg-muted/30',
  iconColor: 'text-muted-foreground',
  Icon: Info,
};

/**
 * Summary card rendered above the existing Health-tab detail cards. Pulls
 * structured flags from `useAnalyticsExperimentHealth` and renders one chip
 * per flag type plus a compact outlier-reason breakdown. Falls back to a
 * "healthy" state when analytics returns no flags; renders nothing when the
 * request errors (the legacy detail cards already cover the offline case).
 */
export function AnalyticsHealthSummary({ experimentId, sensitivity }: Props) {
  const { t } = useTranslation('experiments');
  const { data, isLoading, isError } = useAnalyticsExperimentHealth(experimentId, {
    sensitivity_level: sensitivity,
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="space-y-3 p-6">
          <Skeleton className="h-5 w-48" />
          <div className="grid gap-3 md:grid-cols-3">
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isError || !data) return null;

  const flags = data.flags.filter((flag) => flag.type !== 'outlier_rate');
  const breakdown = data.outlier_breakdown;
  const isHealthy = flags.length === 0;

  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-[16px] font-medium">{t('detail.health.summary.title')}</h3>
          <p className="text-muted-foreground text-xs">
            {t('detail.health.summary.sensitivityLevel', { level: data.sensitivity_level })}
          </p>
        </div>

        {isHealthy ? (
          <div className="text-success flex items-center gap-2 text-sm">
            <CheckCircle2 className="h-4 w-4" aria-hidden />
            {t('detail.health.summary.healthy')}
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {flags.map((flag, idx) => (
              <HealthFlagChip key={`${flag.type}-${flag.severity}-${String(idx)}`} flag={flag} />
            ))}
          </div>
        )}

        {breakdown.length > 0 ? (
          <div className="border-border space-y-1.5 border-t pt-3">
            <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
              {t('detail.health.summary.outlierReasons')}
            </p>
            <div className="flex flex-wrap gap-2">
              {breakdown.map((reason) => (
                <div
                  key={reason.reason}
                  className="bg-muted text-foreground rounded-md px-2 py-1 text-xs"
                >
                  <span className="font-medium">{reason.reason}</span>
                  <span className="text-muted-foreground ml-1">
                    {String(reason.count)} ({reason.percentage.toFixed(1)}%)
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

/**
 * For `traffic_split_drift` flags, calibrates severity client-side using the
 * `worst_drift_pp` value from the payload rather than trusting the backend's
 * severity verbatim — the backend may not have access to the same thresholds
 * the UI treats as actionable.
 */
function resolveDriftSeverity(
  payload: Record<string, unknown> | undefined,
): AnalyticsHealthFlagSeverity {
  const pp = typeof payload?.worst_drift_pp === 'number' ? payload.worst_drift_pp : null;
  if (pp === null) return 'warning';
  if (pp < 15) return 'info';
  if (pp <= 30) return 'warning';
  return 'critical';
}

/** One flag chip — encapsulates the type-label + severity-style lookups + payload summary. */
function HealthFlagChip({ flag }: { flag: AnalyticsHealthFlag }) {
  const { t } = useTranslation('experiments');
  const isDrift = flag.type === 'traffic_split_drift';

  const title = healthFlagTitle(flag);
  const label = t(title.key, title.params);
  const message = healthFlagMessage(flag);

  // Calibrate severity client-side for drift flags; use backend value for all others.
  const effectiveSeverity: AnalyticsHealthFlagSeverity = isDrift
    ? resolveDriftSeverity(flag.payload)
    : (flag.severity as AnalyticsHealthFlagSeverity);

  const styles = SEVERITY_STYLES[effectiveSeverity] ?? FALLBACK_SEVERITY_STYLE;
  const { Icon } = styles;

  // The backend's own explanation wins; the structured renderers cover flags without one.
  const detail = message ? (
    <p className="text-muted-foreground mt-0.5 text-xs">{message}</p>
  ) : isDrift ? (
    renderTrafficSplitDrift(flag.payload)
  ) : (
    summarizePayload(flag.payload)
  );

  return (
    <div className={cn('flex gap-3 rounded-lg border p-3', styles.wrap)}>
      <Icon className={cn('mt-0.5 h-4 w-4 flex-shrink-0', styles.iconColor)} aria-hidden />
      <div className="min-w-0">
        <div className="flex items-center gap-1">
          <p className="text-sm font-medium">{label}</p>
          {isDrift && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground cursor-pointer"
                    aria-label={t('detail.health.summary.driftTooltip')}
                  >
                    <HelpCircle className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs text-xs">
                  {t('detail.health.summary.driftTooltip')}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        {detail}
      </div>
    </div>
  );
}

/**
 * Human-readable detail for `traffic_split_drift` flags.
 *
 * Converts raw payload like:
 *   `configured: [50,25,25]  actual: [["A",58],["B",57],["CONTROL",87]]  worst_drift_pp: 21.29`
 * into two readable lines:
 *   "Worst arm off by 21.3pp from target"
 *   "A 29% · B 28% · CONTROL 43%  (target: 50 / 25 / 25)"
 */
function renderTrafficSplitDrift(
  payload: Record<string, unknown> | undefined,
): React.ReactElement | null {
  if (!payload) return null;

  const worstDrift = typeof payload.worst_drift_pp === 'number' ? payload.worst_drift_pp : null;

  const configured =
    Array.isArray(payload.configured) &&
    (payload.configured as unknown[]).every((v) => typeof v === 'number')
      ? (payload.configured as number[])
      : null;

  // actual arrives as [["A", 58], ["B", 57], ...] — validate the shape.
  const actual =
    Array.isArray(payload.actual) &&
    (payload.actual as unknown[]).every(
      (v) =>
        Array.isArray(v) && v.length === 2 && typeof v[0] === 'string' && typeof v[1] === 'number',
    )
      ? (payload.actual as [string, number][])
      : null;

  const total = actual ? actual.reduce((s, [, n]) => s + n, 0) : 0;

  const line1 =
    worstDrift !== null ? `Worst arm off by ${worstDrift.toFixed(1)}pp from target` : null;

  const actualStr =
    actual && total > 0
      ? actual.map(([arm, n]) => `${arm} ${String(Math.round((n / total) * 100))}%`).join(' · ')
      : null;

  const configuredStr = configured ? configured.join(' / ') : null;

  const line2 = [actualStr, configuredStr ? `(target: ${configuredStr})` : null]
    .filter(Boolean)
    .join('  ');

  if (!line1 && !line2) return null;

  return (
    <div className="text-muted-foreground mt-0.5 space-y-0.5 text-xs">
      {line1 && <p>{line1}</p>}
      {line2 && <p>{line2}</p>}
    </div>
  );
}

/** Best-effort one-liner for flags that don't have a dedicated renderer. */
function summarizePayload(payload: Record<string, unknown> | undefined): React.ReactElement | null {
  if (!payload) return null;
  const entries = Object.entries(payload).slice(0, 3);
  if (entries.length === 0) return null;
  const text = entries.map(([k, v]) => `${k}: ${formatVal(v)}`).join(' · ');
  return <p className="text-muted-foreground mt-0.5 text-xs">{text}</p>;
}

function formatVal(v: unknown): string {
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v.toFixed(2);
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v.slice(0, 40);
  return JSON.stringify(v).slice(0, 40);
}
